/**
 * EVENTSFLO-ADMIN-BACKUP-RESTORE-RUNS-HISTORY-V1
 *
 * Read-only "Restore Runs" section for the SuperAdmin Backups page.
 * Calls `GET /api/admin/backup/restore-runs` (allow-listed item
 * projection only) and renders a compact shadcn Table with refresh
 * + prev/next pagination. NO mutating buttons.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listRestoreRuns } from "./backupApi";
import { formatDate, PAGE_SIZE } from "./backupFormatters";

const _STATUS_COLOR = {
  ok: "bg-green-500/20 text-green-400 border-green-500/30",
  completed_with_errors: "bg-amber-500/20 text-amber-400 border-amber-500/30",
};

function StatusBadge({ status }) {
  const cls = _STATUS_COLOR[status] || "bg-zinc-500/20 text-zinc-300 border-zinc-500/30";
  return <Badge className={cls}>{status || "—"}</Badge>;
}

function shortId(id) {
  if (!id) return "—";
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export default function BackupRestoreRunsSection() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchRuns = useCallback(async (newOffset) => {
    setLoading(true);
    try {
      const { data } = await listRestoreRuns({
        limit: PAGE_SIZE,
        offset: newOffset,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load restore runs");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns(0);
  }, [fetchRuns]);

  const handleRefresh = () => fetchRuns(offset);
  const handlePrev = () => {
    const o = Math.max(0, offset - PAGE_SIZE);
    setOffset(o);
    fetchRuns(o);
  };
  const handleNext = () => {
    const o = offset + PAGE_SIZE;
    setOffset(o);
    fetchRuns(o);
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card data-testid="restore-runs-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-4 h-4" /> Isolated restore runs ({total})
        </CardTitle>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>
            Page {currentPage} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrev}
            disabled={offset === 0}
            data-testid="restore-runs-prev-btn"
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={offset + PAGE_SIZE >= total}
            data-testid="restore-runs-next-btn"
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            data-testid="refresh-restore-runs-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-zinc-400">Loading…</div>
        ) : items.length === 0 ? (
          <div
            className="text-sm text-zinc-400 py-8 text-center"
            data-testid="restore-runs-empty-state"
          >
            No isolated restore runs yet.
          </div>
        ) : (
          <Table data-testid="restore-runs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Backup</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started → Completed</TableHead>
                <TableHead>Docs</TableHead>
                <TableHead>Coll.</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Live DB mutated</TableHead>
                <TableHead>Two-person</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow
                  key={r.restore_session_id}
                  data-testid={`restore-run-row-${r.restore_session_id}`}
                >
                  <TableCell
                    className="font-mono text-xs"
                    title={r.backup_id}
                  >
                    {shortId(r.backup_id)}
                  </TableCell>
                  <TableCell className="text-xs">{r.isolated_target_label}</TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{formatDate(r.started_at)}</div>
                    <div className="text-zinc-500">→ {formatDate(r.completed_at)}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {(r.documents_inserted ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.collections_restored_count ?? 0}
                  </TableCell>
                  <TableCell className="text-xs">{r.actor_email || "—"}</TableCell>
                  <TableCell>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      {r.live_database_mutated ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.two_person_approval_status || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
